import store from "../../redux/store";
import Http from "../http";
import i18n from "../../i18n";

// SAM-257: pdfResize and s3Delete return a `{ success: false }` shape on
// failure rather than a truthy `[]`. Both endpoints reply `{ success }`
// (asset.controller.ts `pdf-resize/:id`, and v1 s3_controller.js `delete`).
// deleteAsset already did this; reads keep `[]`/`null`.
import HmacSHA256 from "crypto-js/hmac-sha256";

export const s3Upload = async (
  params,
  targetSize = null,
  isPrivate = false,
  targetUser = null
) => {
  let path = `/v2/asset/upload`;
  let queryStringApplied = false;
  if (targetSize) {
    path += `?target=${targetSize}`;
    queryStringApplied = true;
  }

  if (isPrivate) {
    path += `${queryStringApplied ? "&" : "?"}private_asset=true`;
    queryStringApplied = true;
  }

  if (targetUser) {
    path += `${queryStringApplied ? "&" : "?"}target_user=${targetUser}`;
    queryStringApplied = true;
  }
  let response;
  try {
    response = await Http.sharedInstance.withAuth().post(path, params, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (e) {
    const status = e?.response?.status;
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      "Upload failed";

    alert(`Upload failed${status ? ` (${status})` : ""}: ${message}`);
    throw new Error(`Upload failed${status ? ` (${status})` : ""}: ${message}`);
  }

  // SAM-309: a 200 is not proof of an upload. `asset.controller.ts`'s upload
  // handler swallows a failed PDF first-page extraction into `console.error`
  // and then does `res.send(result)` with `result` still undefined — HTTP 200,
  // empty body. Every caller reads `.assetId` straight off this, so that
  // answer became `/v2/asset/<id>/undefined`, `avatar: undefined`, or an icon
  // painted over nothing — in five places, none of which said anything.
  // Refusing here closes all of them at once, and every caller already has to
  // survive this function throwing: the catch above has done so since the
  // alert was added.
  if (!response.data || !response.data.assetId) {
    const message = i18n.t("default:_UPLOAD_RETURNED_NO_ASSET");
    alert(message);
    throw new Error(message);
  }

  return response.data;
};

export const pdfResize = async (params) => {
  let path = `/v2/asset/pdf-resize/${params.id}`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path);
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const getPdfPages = async (params) => {
  const { query } = params;
  let path = `/v2/asset/pdf-images?${query}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);
    return response.data;
  } catch (e) {
    return [];
  }
};

const NON_EXISTING_ASSETS = [];
const EXISTING_ASSETS = [];
export const assetExists = (assetUrl) => {
  if (NON_EXISTING_ASSETS.indexOf(assetUrl) !== -1) {
    return false;
  }

  if (EXISTING_ASSETS.indexOf(assetUrl) !== -1) {
    return true;
  }

  try {
    const http = new XMLHttpRequest();

    http.open("HEAD", assetUrl, false);
    http.send();

    return http.status !== 404;
  } catch (e) {
    NON_EXISTING_ASSETS.push(assetUrl);
  }
};

// SAM-150 — an absolute URL whose host is the object store is not an app URL.
// Handing one to the browser bypasses /v2/asset/auth/download entirely: no
// fileAuth, no tenant check, nothing but the bucket's own ACL standing between
// the file and anyone holding the link. Stored records still carry these
// (pre-2021 `assets.location` rows, and every `assets.thumbnail`), so the guard
// belongs in the two helpers all ~156 asset URLs in this app route through
// rather than at the call sites.
//
// ponytail: refuse, don't rewrite — a bucket URL carries an object key, not an
// asset ID, so there is nothing to rewrite it *to*. An empty src/href renders
// as a broken image or a dead link, which is what these already are: the bucket
// answers AccessDenied to an unauthenticated browser.
const isStorageUrl = (value) =>
  typeof value === "string" &&
  /^https?:\/\/[^/]*(digitaloceanspaces\.com|amazonaws\.com)/i.test(value);

//TODO: this method seems unsecure, not even salt, check and find out who's using it (PDF preview for example)
export const getAssetKey = (assetUrl) => {
  if (isStorageUrl(assetUrl)) {
    return "";
  }

  let origin = window.location.origin;

  if (origin.startsWith("http://") || origin.startsWith("https://")) {
    origin = origin.split("://")[1];
  }

  let originParts = origin.split(".");

  if (originParts[originParts.length - 1] === "localhost") {
    originParts = [null].concat(originParts);
  }

  const assetId = assetUrl.split("/").pop();

  let keyString = "";

  if (originParts.length >= 2) {
    const host = originParts[0];
    const key = `host:${host};assetId:${assetId}`;

    keyString += `${assetUrl.indexOf("?") !== -1 ? "&" : "?"}key=${Buffer.from(
      key
    ).toString("base64")}`;
  }

  return assetUrl + keyString;
};

export const getFileTarget = (assetId, target, preview = false) => {
  if (!assetId) {
    return "";
  }

  // SAM-200. Some records hold the *whole S3 upload response object* where an
  // asset id belongs — the write bug SAM-091 fixed, left behind in rows saved
  // before it did (confirmed read-only 2026-09-10: 2 job types in `pink`, 1 in
  // `magenta`, each with keys ETag/Location/key/Key/Bucket/assetId/...).
  //
  // These fell into the `Location` branch below and were refused, because a
  // Spaces URL is not an app URL — correct for a bare URL, but this object is
  // not one. It carries `assetId`, so the SAM-150 note's "there is nothing to
  // rewrite it *to*" does not hold here: there is, and resolving through it
  // makes the icon render again on every surface (job-type list and header,
  // and the activity side panel this was reported against) for every tenant,
  // without a data migration.
  if (typeof assetId === "object" && assetId.assetId) {
    return getFileTarget(assetId.assetId, target, preview);
  }

  let questionAdded = false;

  let url = "/v2/asset/auth/download/" + assetId;

  if (typeof assetId === "object" && assetId.Location) {
    return isStorageUrl(assetId.Location) ? "" : assetId.Location;
  }

  if (isStorageUrl(assetId)) {
    return "";
  }

  if (
    assetId &&
    (assetId.toString().startsWith("http") ||
      assetId.toString().startsWith("/static"))
  ) {
    if (assetId.indexOf("-lg") !== -1) {
      return assetId.replace("-lg", "-" + target);
    }
    return assetId;
  }
  if (target) {
    url += "?size=" + target;
    questionAdded = true;
  }

  if (preview) {
    url += `${questionAdded ? "&" : "?"}preview=true`;
    questionAdded = true;
  }

  const state = store.getState();
  const token =
    state.user &&
      state.user.access_token &&
      state.user.access_token.accessToken
      ? state.user.access_token.accessToken
      : null;
  const profile = state.user && state.user.profile ? state.user.profile : null;
  const userId = profile && (profile.id || profile._id)
    ? (profile.id || profile._id).toString()
    : null;

  if (!token || !userId) {
    return url;
  }

  const payload = `${assetId}`;
  const sig = HmacSHA256(payload, token).toString();

  url += `${questionAdded ? "&" : "?"}fileAuth=${encodeURIComponent(
    `${userId}.${sig}`
  )}`;

  return url;
};

export const getFileInfo = async (assetId) => {
  if (typeof assetId === "object") {
    return new Promise((resolve) => resolve(assetId));
  }
  const path = `/v2/asset/info/${assetId}`;

  try {
    const response = await Http.sharedInstance.withAuth().get(path);

    return response.data;
  } catch (e) {
    return null;
  }
};

export const s3Delete = async (params) => {
  const path = `/v2/s3/delete`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return response.data;
  } catch (e) {
    return { success: false };
  }
};

export const deleteAsset = async (assetId) => {
  const path = `/v2/asset/${assetId}`;

  try {
    const response = await Http.sharedInstance.withAuth().delete(path);

    return response.data;
  } catch (e) {
    return { success: false };
  }
};
